const EARTH_RADIUS_METERS = 6371008.8;

export const FREQUENT_PLACE_RADIUS_METERS = 200;
export const FREQUENT_PLACE_MIN_VISITS = 2;
export const FREQUENT_PLACE_CHECKIN_MINUTES = 45;
export const FREQUENT_PLACE_ROUTE_POINT_MINUTES = 5;

const ROUTE_DATA_KEYS = ['health', 'moves', 'googleMaps'];

const degreesToRadians = (value) => (value * Math.PI) / 180;

const toIsoDate = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const normalizeCoords = (lat, lon) => {
  const numericLat = Number(lat);
  const numericLon = Number(lon);
  if (!Number.isFinite(numericLat) || !Number.isFinite(numericLon)) return null;

  const normalized = numericLat < -90 || numericLat > 90
    ? [numericLon, numericLat]
    : [numericLat, numericLon];

  const [finalLat, finalLon] = normalized;
  if (finalLat < -90 || finalLat > 90 || finalLon < -180 || finalLon > 180) return null;
  return { lat: finalLat, lon: finalLon };
};

const getDistanceMeters = (start, end) => {
  const latitudeDelta = degreesToRadians(end.lat - start.lat);
  const longitudeDelta = degreesToRadians(end.lon - start.lon);
  const startLatitude = degreesToRadians(start.lat);
  const endLatitude = degreesToRadians(end.lat);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(startLatitude) * Math.cos(endLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  const clamped = Math.min(1, Math.max(0, haversine));

  return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(clamped), Math.sqrt(1 - clamped));
};

const inferKindLabel = (item = {}) => {
  const haystack = `${item.name || ''} ${item.category || ''}`.toLowerCase();
  if (haystack.includes('casa') || haystack.includes('home')) return 'Casa';
  if (haystack.includes('trabalho') || haystack.includes('office') || haystack.includes('cowork')) return 'Trabalho';
  if (haystack.includes('academia') || haystack.includes('gym') || haystack.includes('fitness')) return 'Academia';
  if (haystack.includes('cafe') || haystack.includes('caf') || haystack.includes('coffee')) return 'Café';
  if (haystack.includes('restaurante') || haystack.includes('restaurant') || haystack.includes('bar')) return 'Comida';
  return 'Lugar';
};

const pickClusterName = (nameCounts, kindLabel) => {
  const winner = [...nameCounts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];

  if (winner?.[0]) return winner[0];
  return `${kindLabel} frequente`;
};

const getRouteRepresentativePoints = (route = []) => {
  if (!Array.isArray(route) || route.length < 2) return [];

  const validPoints = route
    .map((point) => normalizeCoords(point?.lat, point?.lon))
    .filter(Boolean);

  if (validPoints.length < 2) return [];

  const firstPoint = route[0] || {};
  const routeDate = toIsoDate(firstPoint.route_date || firstPoint.date);
  const durationMinutes = Math.max(
    FREQUENT_PLACE_ROUTE_POINT_MINUTES,
    Math.round((Number(firstPoint.duration) || 0) / 120)
  );
  const routeType = firstPoint.route_type || 'health';

  return [
    {
      ...validPoints[0],
      date: routeDate,
      minutes: durationMinutes,
      source: 'route',
      routeType,
      name: '',
      category: routeType,
    },
    {
      ...validPoints[validPoints.length - 1],
      date: routeDate,
      minutes: durationMinutes,
      source: 'route',
      routeType,
      name: '',
      category: routeType,
    },
  ];
};

export const buildFrequentPlaceInputs = (data = {}) => {
  const items = [];

  (data.checkins || []).forEach((checkin) => {
    const coords = normalizeCoords(checkin?.lat, checkin?.lon);
    if (!coords) return;

    items.push({
      ...coords,
      date: toIsoDate(checkin?.date || checkin?.created_at),
      minutes: FREQUENT_PLACE_CHECKIN_MINUTES,
      source: 'checkin',
      routeType: '',
      name: String(checkin?.venue || checkin?.name || '').trim(),
      category: String(checkin?.category || '').trim(),
    });
  });

  ROUTE_DATA_KEYS.forEach((dataKey) => {
    (data[dataKey] || []).forEach((route) => {
      getRouteRepresentativePoints(route).forEach((point) => items.push(point));
    });
  });

  return items;
};

export const computeFrequentPlaces = (
  items = [],
  {
    radiusMeters = FREQUENT_PLACE_RADIUS_METERS,
    minVisits = FREQUENT_PLACE_MIN_VISITS,
    topN = 10,
  } = {}
) => {
  const clusters = [];

  (items || []).forEach((item) => {
    const coords = normalizeCoords(item?.lat, item?.lon);
    if (!coords) return;

    const matchingCluster = clusters
      .map((cluster) => ({
        cluster,
        distance: getDistanceMeters(coords, cluster),
      }))
      .filter(({ distance }) => distance <= radiusMeters)
      .sort((a, b) => a.distance - b.distance)[0]?.cluster;

    const minutes = Math.max(0, Number(item?.minutes) || 0);
    const date = toIsoDate(item?.date);
    const kindLabel = inferKindLabel(item);
    const name = String(item?.name || '').trim();

    if (!matchingCluster) {
      const nameCounts = new Map();
      if (name) nameCounts.set(name, 1);

      clusters.push({
        lat: coords.lat,
        lon: coords.lon,
        count: 1,
        totalMinutes: minutes,
        lastDate: date,
        kindLabel,
        category: String(item?.category || '').trim(),
        sourceBreakdown: {
          checkin: item?.source === 'checkin' ? 1 : 0,
          route: item?.source === 'route' ? 1 : 0,
        },
        nameCounts,
      });
      return;
    }

    matchingCluster.lat = ((matchingCluster.lat * matchingCluster.count) + coords.lat) / (matchingCluster.count + 1);
    matchingCluster.lon = ((matchingCluster.lon * matchingCluster.count) + coords.lon) / (matchingCluster.count + 1);
    matchingCluster.count += 1;
    matchingCluster.totalMinutes += minutes;
    matchingCluster.lastDate = [matchingCluster.lastDate, date].filter(Boolean).sort().at(-1) || '';
    if (name) {
      matchingCluster.nameCounts.set(name, (matchingCluster.nameCounts.get(name) || 0) + 1);
    }
    matchingCluster.sourceBreakdown[item?.source === 'checkin' ? 'checkin' : 'route'] += 1;
    if (!matchingCluster.category && item?.category) {
      matchingCluster.category = String(item.category).trim();
    }
  });

  return clusters
    .filter((cluster) => cluster.count >= minVisits)
    .map((cluster) => ({
      nameApprox: pickClusterName(cluster.nameCounts, cluster.kindLabel),
      lat: Number(cluster.lat.toFixed(6)),
      lon: Number(cluster.lon.toFixed(6)),
      count: cluster.count,
      lastDate: cluster.lastDate,
      totalMinutes: cluster.totalMinutes,
      kindLabel: cluster.kindLabel,
      category: cluster.category,
      sourceBreakdown: cluster.sourceBreakdown,
    }))
    .sort((a, b) => b.count - a.count || b.totalMinutes - a.totalMinutes || b.lastDate.localeCompare(a.lastDate))
    .slice(0, topN);
};
